import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Box, Grid, Stack, Typography } from '@mui/material';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { venueGetOfferApi } from '../../api/offers/venueGetOfferApi';
import { venueCreateComplaintApi } from '../../api/reservations/venueCreateComplaintApi';
import Button from '../../components/Button/Button';
import LoadingButton from '../../components/Button/LoadingButton';
import { SHIPPING_AVAILABILITY } from '../../components/Dialogs/CollabsFilters/options';
import { useConfirm } from '../../components/Dialogs/Confirm/context/ConfirmDialog.context';
import { OfferSurveyDialog } from '../../components/Dialogs/OfferSurveyDialog/OfferSurveyDialog';
import { mapToApiOfferSurveyPayload } from '../../components/Dialogs/ReportReservations/components/schema';
import { GoBackV2 } from '../../components/GoBack/GoBackV2';
import LabelDetail from '../../components/LabelDetail/LabelDetail';
import StatusLabel from '../../components/StatusLabel/StatusLabel';
import { Panel } from '../../components/TabPanelContainer/TabPanelContainer';
import ReservationsTable from '../../components/Tables/Reservation/Reservation';
import { ComplaintItemInput, Offer, OfferCategory, OfferStatus } from '../../graphql/API';
import { useUpdateOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useAppSelector } from '../../redux/hooks';
import { datetimeWithTimezone, getTimeInTimezone } from '../../utils/dateV2';
import { formatNumber, formatNumberWithCommas } from '../../utils/formatter';
import OfferDetailSkeleton from './OfferDetailSkeleton';
import Access from './components/Access';
import BorderBox from './components/BorderBox';
import LabelDay from './components/LabelDay';
import Requirement from './components/Requirement';
import './style.css';

const waitTimeOptions = [
  { value: 'Everyday', key: '0' },
  { value: '3 times per week', key: '2' },
  { value: '1 time per week', key: '6' },
  { value: '1 time every two weeks', key: '13' },
  { value: '1 time per month', key: '30' },
  { value: '1 time every 2 months', key: '60' },
  { value: '1 time per year', key: '364' },
  { value: '1 time only', key: '10000' },
];

const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const OfferDetail: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const venueState = useAppSelector((state) => state.venue.value);
  const venueId = venueState?.id;
  const nagivate = useNavigate();

  const { confirm } = useConfirm();

  const [surveyPopup, setSurveyPopup] = React.useState(false);
  const [isSurveyLoading, setIsSurveyLoading] = React.useState(false);
  const updateMutation = useUpdateOfferQuery(venueId);

  const { offerId } = useParams<{ offerId: string }>();

  const { data, isLoading } = useQuery<Offer>({
    queryKey: ['offer-detail', offerId],
    queryFn: async ({ queryKey }) => {
      const [, offerID] = queryKey as [string, string];

      return venueGetOfferApi(offerID);
    },
    placeholderData: keepPreviousData,
  });

  const frequency = React.useMemo(
    () =>
      data
        ? waitTimeOptions.find((option) => option.key === String(data.waitTime))?.value
        : undefined,
    [data],
  );

  const panels = React.useMemo<Panel[]>(
    () => [
      {
        label: 'Reservations',
        children: <ReservationsTable height='70vh' filters={{ offerID: offerId }} />,
      },
    ],
    [offerId],
  );

  const handleOfferSurvey = () => {
    setSurveyPopup((prev) => !prev);
  };

  const handleOfferDelete = React.useCallback(
    async (questions: ComplaintItemInput[]) => {
      if (offerId) {
        setSurveyPopup((prev) => !prev);
        setIsSurveyLoading(true);
        const apiPayload = mapToApiOfferSurveyPayload(questions || [], {
          venueId: venueState?.id || '',
          plan: venueState?.premium ? 'PREMIUM' : 'BASIC',
          venueName: venueState?.name || '',
          email: venueState?.email || '',
          venueLink: venueState?.id || '',
          type: 'Venue canceled offer survey',
        });
        console.log(apiPayload, 'api payload');
        await venueCreateComplaintApi(apiPayload);
        await updateMutation.mutateAsync({ id: offerId, status: OfferStatus.deleted });
        setIsSurveyLoading(false);
        enqueueSnackbar('Offer cancelled, Thank you for providing us with your valuable feedback', {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        nagivate(data?.category === OfferCategory.product ? `/products` : `/offers`);
      }
    },
    [
      offerId,
      venueState?.id,
      venueState?.premium,
      venueState?.name,
      venueState?.email,
      updateMutation,
      enqueueSnackbar,
      nagivate,
      data?.category,
    ],
  );

  const handleOfferReactive = React.useCallback(async () => {
    if (offerId) {
      const isConfirm = await confirm({
        title: 'Reactivate Offer',
        confirmationText: 'Reactivate',
        description: <>Are you sure you want to activate this offer?</>,
      });

      if (isConfirm) {
        await updateMutation.mutateAsync({ id: offerId, status: OfferStatus.active });
        nagivate(data?.category === OfferCategory.product ? `/products` : `/offers`);
      }
    }
  }, [offerId, updateMutation, nagivate, confirm, data?.category]);

  const renderButton = () => {
    if (!data) {
      return null;
    }

    if ([OfferStatus.cancelled, OfferStatus.deleted].includes(data.status)) {
      return (
        <>
          <Button
            component={Link}
            to={
              data.category === OfferCategory.product
                ? `/products/${data.id}/edit`
                : `/offers/${data.id}/edit`
            }
            id='btn-edit'
            variant='outlined'
            size='large'
            color='primary'
            sx={{ py: 1, px: 3 }}
          >
            Edit
          </Button>
          <LoadingButton
            id='btn-delete'
            size='large'
            variant='outlined'
            color='primary'
            onClick={handleOfferReactive}
            loading={updateMutation.isPending}
            sx={{ py: 1, px: 3 }}
          >
            Reactivate
          </LoadingButton>
        </>
      );
    }

    return (
      <>
        <Button
          component={Link}
          to={
            data.category === OfferCategory.product
              ? `/products/${data.id}/edit`
              : `/offers/${data.id}/edit`
          }
          id='btn-edit'
          startIcon={<EditIcon />}
          variant='outlined'
          size='medium'
          color='primary'
          sx={{ py: 1, px: 3 }}
        >
          Edit
        </Button>
        <LoadingButton
          id='btn-delete'
          variant='outlined'
          size='medium'
          onClick={handleOfferSurvey}
          color='inherit'
          loading={updateMutation.isPending || isSurveyLoading}
          sx={{ minWidth: 80, color: '#E05757', borderColor: '#E05757', py: 1, px: 3 }}
        >
          Delete
        </LoadingButton>
      </>
    );
  };

  const timezoneId = React.useMemo(() => data?.category === OfferCategory.event && data?.offerTimeZoneId ? data?.offerTimeZoneId : data?.timeZoneId, [data?.category, data?.offerTimeZoneId, data?.timeZoneId]);

  const { startDatetime, endDatetime } = React.useMemo(
    () => ({
      startDatetime: datetimeWithTimezone({
        date: data?.startDate,
        time: data?.startTime,
        timezoneId: timezoneId,
      }).tz(),
      endDatetime: datetimeWithTimezone({
        date: data?.startDate,
        time: data?.endTime,
        timezoneId: timezoneId,
      }).tz(),
    }),
    [data, timezoneId],
  );

  return (
    <Box
      px={4}
      py={2}
      sx={{
        backgroundColor: 'white',
        flex: 1,
        overflowY: 'hidden',
        height: '100vh',
      }}
    >
      <Grid container spacing={6} sx={{ height: '100%' }}>
        <Grid
          item
          xs={3.7}
          sx={{ height: '100%', overflowY: 'auto' }}
          className='offer-detail-container'
        >
          {!isLoading && data ? (
            <Box py={1.4} pr={4} sx={{ borderRight: '1px solid #F2EFEF' }}>
              <Box display={'flex'} alignItems={'center'} mb={3}>
                <GoBackV2
                  title='Back'
                  to={data.category === OfferCategory.product ? '/products' : '/offers'}
                />
              </Box>
              <Box sx={{ maxWidth: '100%', height: '265px' }}>
                <Avatar
                  variant='rounded'
                  alt={String(data.title)}
                  src={String(data.picture?.medium)}
                  sx={{ height: '100%', width: '100%', objectFit: 'contain' }}
                />
              </Box>

              <Stack spacing={2} sx={{ mt: 4 }}>
                <BorderBox title='Offer details'>
                  <LabelDetail label='Name' description={data.title} />
                  <LabelDetail label='Description' description={data.description} />
                  <LabelDetail
                    label='Status'
                    description={<StatusLabel type='offer' status={data.status} emptyBackground />}
                  />
                  {data.category === OfferCategory.product && (
                    <>
                      <LabelDetail
                        label='Product value'
                        description={`${venueState?.currency} ${formatNumberWithCommas(
                          data?.offerValue || 0,
                        )}`}
                      />
                      <LabelDetail
                        label='Incentive'
                        description={`${venueState?.currency} ${formatNumberWithCommas(
                          data?.incentiveValue || 0,
                        )}`}
                      />
                      <LabelDetail
                        label='Minimum followers'
                        description={formatNumber(data?.minFollowers ?? 0)}
                      />
                      <LabelDetail
                        label='Shipping availability'
                        description={data?.shippingRegions
                          ?.map((region) => region && SHIPPING_AVAILABILITY[region])
                          .join(', ')}
                      />
                    </>
                  )}
                  {!data.private && data.category !== OfferCategory.product && (
                    <>
                      <LabelDetail label='Spots' description={String(data.spots)} />
                      {data.category !== OfferCategory.event && (
                        <LabelDetail label='Frequency' description={frequency} />
                      )}
                      <LabelDetail
                        label={data.category === OfferCategory.event ? 'Event date' : 'Offer Start'}
                        description={
                          data.category === OfferCategory.event
                            ? `${data.availableDays
                                ?.map((day) => capitalizeWords(day || ''))
                                .join('')} ${startDatetime.format('ll')}`
                            : startDatetime.format('ll')
                        }
                      />
                      {data.category !== OfferCategory.event && (
                        <LabelDetail
                          label='Offer End'
                          description={data.endDate ? endDatetime.format('ll') : '-'}
                        />
                      )}
                    </>
                  )}
                  {data.category === OfferCategory.event && (
                        <LabelDetail
                          label='Event location'
                          description={data.address}
                        />
                      )}
                  {data.private || data.category === OfferCategory.product ? null : (
                    <>
                      <LabelDetail
                        label='Start time'
                        description={getTimeInTimezone(
                          data?.startDate,
                          data?.startTime,
                          timezoneId,
                        )}
                      />
                      <LabelDetail
                        label='End time'
                        description={getTimeInTimezone(
                          data?.startDate,
                          data?.endTime,
                          timezoneId,
                        )}
                      />
                    </>
                  )}
                  {!data.private &&
                    data.category !== OfferCategory.event &&
                    data.category !== OfferCategory.product && (
                      <Box display='flex' flexDirection='column'>
                        <Typography variant='caption' color='text.secondary'>
                          Available Days
                        </Typography>

                        <Grid container spacing={0.5} mt={'12px'}>
                          {data.availableDays?.map((x, i) => (
                            <Grid key={i} item>
                              <LabelDay day={String(x)} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                </BorderBox>

                {!data.private && data.category !== OfferCategory.product && (
                  <div style={{ marginTop: '42px' }}>
                    <BorderBox title='Access'>
                      <Access data={data} />
                    </BorderBox>
                  </div>
                )}

                {!data.private && (
                  <div style={{ marginTop: '42px' }}>
                    <BorderBox title='Requirements'>
                      <Requirement data={data} />
                    </BorderBox>
                  </div>
                )}
              </Stack>

              <Box
                display='flex'
                mt={3}
                justifyContent='space-between'
                alignItems={'center'}
                sx={{ borderTop: '1px solid #F2EFEF', py: 3 }}
              >
                <Stack direction='row' width='75%' spacing={2}>
                  {renderButton()}
                </Stack>
              </Box>
            </Box>
          ) : (
            <OfferDetailSkeleton />
          )}
        </Grid>

        <Grid item xs={8.3} sx={{ height: '100%', overflowY: 'auto' }}>
          <Box py={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Typography
              component={'span'}
              sx={{ fontSize: '18px', fontWeight: '500', mb: 3, display: 'inline-block' }}
            >
              Reservations
            </Typography>
            <ReservationsTable
              height='69vh'
              filters={{ offerID: offerId }}
              showPromotionLimitPopup={false}
            />
          </Box>
        </Grid>
      </Grid>

      <OfferSurveyDialog
        open={surveyPopup}
        onClose={handleOfferSurvey}
        isPrivateOffer={data?.private || false}
        onSubmitQuestions={handleOfferDelete}
      />
    </Box>
  );
};

export default OfferDetail;
